import React, { useEffect, useRef, useState, useCallback } from 'react'
import '../styles/Testimonials.css'
import { T } from './Translation'

function Testimonials() {
  const testimonialsRef = useRef(null)
  const desktopTrackRef = useRef(null)
  const mobileTrackRef = useRef(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [mobileIndex, setMobileIndex] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)
  const [isAnimating, setIsAnimating] = useState(false)

  const testimonials = [
    {
      textKey: 'testimonial1Text',
      authorKey: 'testimonial1Author',
      avatar: '👰'
    },
    {
      textKey: 'testimonial2Text',
      authorKey: 'testimonial2Author',
      avatar: '🤵'
    },
    {
      textKey: 'testimonial3Text',
      authorKey: 'testimonial3Author',
      avatar: '💑'
    },
    {
      textKey: 'testimonial1Text',
      authorKey: 'testimonial1Author',
      avatar: '🎉'
    },
    {
      textKey: 'testimonial2Text',
      authorKey: 'testimonial2Author',
      avatar: '💒'
    }
  ]

  const desktopCount = Math.max(1, testimonials.length - 2)

  // Hash anchor navigation
  useEffect(() => {
    if (window.location.hash === '#testimonials' && testimonialsRef.current) {
      testimonialsRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  // Desktop auto-play
  useEffect(() => {
    if (!isAutoPlaying) return
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % desktopCount)
    }, 4000)
    return () => clearInterval(interval)
  }, [isAutoPlaying, desktopCount])

  // Sync mobile scroll position → mobileIndex for dots
  useEffect(() => {
    const track = mobileTrackRef.current
    if (!track) return

    const onScroll = () => {
      const index = Math.round(track.scrollLeft / track.offsetWidth)
      setMobileIndex(Math.min(Math.max(index, 0), testimonials.length - 1))
    }

    track.addEventListener('scroll', onScroll, { passive: true })
    return () => track.removeEventListener('scroll', onScroll)
  }, [testimonials.length])

  // Programmatically scroll mobile track to a card
  const scrollMobileTo = useCallback((index) => {
    const track = mobileTrackRef.current
    if (!track) return
    track.scrollTo({ left: index * track.offsetWidth, behavior: 'smooth' })
  }, [])

  // Desktop navigation
  const nextSlide = useCallback(() => {
    if (isAnimating) return
    setIsAutoPlaying(false)
    setIsAnimating(true)
    setCurrentIndex((prev) => (prev + 1) % desktopCount)
    setTimeout(() => setIsAnimating(false), 500)
  }, [isAnimating, desktopCount])

  const prevSlide = useCallback(() => {
    if (isAnimating) return
    setIsAutoPlaying(false)
    setIsAnimating(true)
    setCurrentIndex((prev) => (prev - 1 + desktopCount) % desktopCount)
    setTimeout(() => setIsAnimating(false), 500)
  }, [isAnimating, desktopCount])

  const getVisibleTestimonials = () => {
    if (testimonials.length < 3) return testimonials
    const result = []
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % testimonials.length
      result.push({ ...testimonials[index], position: i })
    }
    return result
  }

  const renderCardContent = (testimonial) => (
    <>
      <div className="quote-watermark">"</div>
      <div className="stars">★★★★★</div>
      <p className="testimonial-text">
        <T k={testimonial.textKey} />
      </p>
      <div className="testimonial-divider" />
      <div className="testimonial-author">
        <div className="author-avatar">
          <span>{testimonial.avatar}</span>
        </div>
        <cite className="author-name">
          <T k={testimonial.authorKey} />
        </cite>
      </div>
    </>
  )

  return (
    <section
      id="testimonials"
      ref={testimonialsRef}
      className="testimonials-section"
    >
      <h2>
        <T k="testimonialsTitle" />
      </h2>
      <div className="testimonials-container">
        <div className="testimonials-carousel">

          {/* Desktop: 3-card track */}
          <div ref={desktopTrackRef} className="testimonials-track testimonials-track-desktop">
            {getVisibleTestimonials().map((testimonial, index) => (
              <div
                key={`${currentIndex}-${index}`}
                className={`testimonial-card ${
                  testimonial.position === 1 ? 'center' :
                  testimonial.position === 0 ? 'left' : 'right'
                }`}
              >
                {renderCardContent(testimonial)}
              </div>
            ))}
          </div>

          {/* Mobile: CSS scroll-snap track — browser handles all physics */}
          <div ref={mobileTrackRef} className="testimonials-track testimonials-track-mobile">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card mobile-snap-card">
                {renderCardContent(testimonial)}
              </div>
            ))}
          </div>

          {/* Desktop arrows */}
          {testimonials.length > 3 && (
            <>
              <button
                className="carousel-arrow prev-arrow"
                onClick={prevSlide}
                aria-label="Previous testimonials"
              >
                ←
              </button>
              <button
                className="carousel-arrow next-arrow"
                onClick={nextSlide}
                aria-label="Next testimonials"
              >
                →
              </button>
            </>
          )}
        </div>

        {/* Desktop dots */}
        {testimonials.length > 3 && (
          <div className="carousel-dots testimonials-dots-desktop">
            {Array.from({ length: desktopCount }, (_, index) => (
              <button
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => {
                  setIsAutoPlaying(false)
                  setCurrentIndex(index)
                }}
                aria-label={`Go to testimonial set ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Mobile dots */}
        <div className="carousel-dots testimonials-dots-mobile">
          {testimonials.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === mobileIndex ? 'active' : ''}`}
              onClick={() => scrollMobileTo(index)}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default Testimonials
